<template>
  <div>
    <!-- <Preloader v-if="isLoading" /> -->
    <LeftSidebar v-if="shouldShowSidebar && !isNotFound" />
    <TopHeader v-if="shouldShowHeader && !isNotFound" />

    <div
      :class="[
        'main-content transition-all flex flex-col overflow-hidden min-h-screen',
        {
          active: stateStoreInstance?.open,
          'padding-minus': shouldShowPaddingZero || isNotFound,
        },
      ]"
    >
      <router-view />
      <div class="grow"></div>
      <!-- <MainFooter v-if="shouldShowFooter && !isNotFound" /> -->
    </div>
    <AddNewTaskPopup />
    <AddNewCardPopup />
    <Toast />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  watchEffect,
  computed,
  watch,
} from "vue";
import stateStore from "@/utils/store";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/store/user";
import { supabase, safeSupabase, safeSupabaseOperation, safeAuthStateChange } from "@/lib/supabaseClient";
import { setLanguage, type SupportedLanguage } from "./i18n";
import emitter from "./utils/emitter";

const direction = ref("ltr");

// import Preloader from "./components/Layouts/Preloader.vue";
import LeftSidebar from "./components/Layouts/LeftSidebar.vue";
import TopHeader from "./components/Layouts/TopHeader/index.vue";
import MainFooter from "./components/Layouts/MainFooter.vue";
import AddNewTaskPopup from "./components/Layouts/AddNewTaskPopup.vue";
import AddNewCardPopup from "./components/Layouts/AddNewCardPopup.vue";
import Toast from "./components/Common/Toast.vue";
// import AddNewCardModal from "./components/Layouts/AddNewCardModal.vue";
// import AddNewFileModal from "./components/Layouts/AddNewFileModal.vue";
// import AddNewOrderModal from "./components/Layouts/AddNewOrderModal.vue";
// import AddNewCategorieModal from "./components/Layouts/AddNewCategorieModal.vue";
// import AddNewContactModal from "./components/Layouts/AddNewContactModal.vue";
// import AddNewCustomerModal from "./components/Layouts/AddNewCustomerModal.vue";
// import AddNewLeadModal from "./components/Layouts/AddNewLeadModal.vue";
// import AddNewDealsModal from "./components/Layouts/AddNewDealsModal.vue";
// import AddNewUserModal from "./components/Layouts/AddNewUserModal.vue";
// import AddNewInstructorsModal from "./components/Layouts/AddNewInstructorsModal.vue";
// import SettingsSidebar from "./components/Layouts/SettingsSidebar/index.vue";

export default defineComponent({
  name: "App",
  components: {
    // Preloader,
    LeftSidebar,
    TopHeader,
    // MainFooter,
    AddNewTaskPopup,
    AddNewCardPopup,
    Toast,
    // AddNewCardModal,
    // AddNewFileModal,
    // AddNewOrderModal,
    // AddNewCategorieModal,
    // AddNewContactModal,
    // AddNewCustomerModal,
    // AddNewLeadModal,
    // AddNewDealsModal,
    // AddNewUserModal,
    // AddNewInstructorsModal,
    // SettingsSidebar,
  },
  setup() {
    const stateStoreInstance = stateStore;
    // const isLoading = ref(true);
    const route = useRoute();
    const router = useRouter();
    const userStore = useUserStore();

    const hiddenRoutes = [
      "/",
      "/features",
      "/team",
      "/faq",
      "/contact",
      "/authentication/sign-in",
      "/authentication/sign-up",
      "/authentication/forgot-password",
      "/authentication/reset-password",
      "/authentication/confirm-email",
      "/authentication/lock-screen",
      "/authentication/logout",
      "/extra-pages/coming-soon",
    ];

    const shouldShowSidebar = computed(
      () => !hiddenRoutes.includes(route.path)
    );
    const shouldShowPaddingZero = computed(() =>
      hiddenRoutes.includes(route.path)
    );
    const shouldShowHeader = computed(() => !hiddenRoutes.includes(route.path));
    const shouldShowDiv = computed(() => !hiddenRoutes.includes(route.path));
    const shouldShowFooter = computed(() => !hiddenRoutes.includes(route.path));

    // Handle wildcard path for 404-like routes
    const isNotFound = computed(() =>
      route.matched.some((record) => record.path === "/:pathMatch(.*)*")
    );
    
    // Setup page refresh handlers
    const setupPageRefreshHandlers = () => {
      // Handler for leads page data refresh
      emitter.on('page:refresh:leads', () => {
        if (route.path.includes('/crm/leads')) {
          console.log('Refreshing leads data');
          // We would normally call an API or store method to refresh leads data
          // This is just a placeholder - implement with actual refresh logic
          userStore.fetchOrganizationSettings();
        }
      });
      
      // Handler for deals page data refresh
      emitter.on('page:refresh:deals', () => {
        if (route.path.includes('/crm/deals')) {
          console.log('Refreshing deals data');
          // We would normally call an API or store method to refresh deals data
          // This is just a placeholder - implement with actual refresh logic
          userStore.fetchOrganizationSettings();
        }
      });
      
      // Handler for customers page data refresh
      emitter.on('page:refresh:customers', () => {
        if (route.path.includes('/crm/customers')) {
          console.log('Refreshing customers data');
          // We would normally call an API or store method to refresh customers data
          // This is just a placeholder - implement with actual refresh logic
          userStore.fetchOrganizationSettings();
        }
      });
      
      // Handler for dashboard page data refresh
      emitter.on('page:refresh:dashboard', () => {
        if (route.path.includes('/dashboard')) {
          console.log('Refreshing dashboard data');
          // We would normally call an API or store method to refresh dashboard data
          // This is just a placeholder - implement with actual refresh logic
          userStore.fetchOrganizationSettings();
        }
      });
    };
    
    const cleanupPageRefreshHandlers = () => {
      emitter.off('page:refresh:leads');
      emitter.off('page:refresh:deals');
      emitter.off('page:refresh:customers');
      emitter.off('page:refresh:dashboard');
    };

    onMounted(async () => {
      // בדיקה אם יש טוקן לאיפוס סיסמה בכתובת URL
      const hash = window.location.hash;
      console.log('App: Checking URL hash for password reset token:', hash);
      
      if (hash && hash.includes('access_token=') && hash.includes('type=recovery') && 
          route.path !== '/authentication/reset-password') {
        console.log('App: Found password reset token, redirecting to reset password page');
        // הפנייה לדף איפוס הסיסמה עם שימור הטוקן
        router.push('/authentication/reset-password' + hash);
        return; // הפסק את המשך הפונקציה כדי למנוע התנגשויות
      }

      // Initialize language and direction
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage && ['en', 'he', 'ar'].includes(savedLanguage)) {
        setLanguage(savedLanguage as SupportedLanguage);
      } else {
        // Try to detect browser language
        const browserLang = navigator.language.split('-')[0] || 'en';
        // Only use browser language if it's one we support
        if (['en', 'he', 'ar'].includes(browserLang)) {
          setLanguage(browserLang as SupportedLanguage);
        } else {
          setLanguage('en'); // Default to English
        }
      }

      watchEffect(() => {
        if (stateStore?.open) {
          document.body.classList.remove("sidebar-show");
          document.body.classList.add("sidebar-hide");
        } else {
          document.body.classList.remove("sidebar-hide");
          document.body.classList.add("sidebar-show");
        }
      });
      
      // Check for authenticated user and fetch organization settings
      try {
        const session = await safeSupabaseOperation(async () => {
          const { data } = await supabase.auth.getSession();
          return data.session;
        });
        
        if (session?.user) {
          // Get user profile with organization ID using safeSupabaseOperation
          const profileData = await safeSupabaseOperation(async () => {
            const { data } = await supabase
              .from('user_profiles')
              .select('org_id')
              .eq('user_id', session.user.id)
              .single();
            return data;
          });
            
          if (profileData?.org_id) {
            // Extract org_id as string explicitly
            const orgId: string = profileData.org_id as string;
            
            // Set user in store
            userStore.setUser({
              id: session.user.id,
              email: session.user.email || '',
              organization_id: orgId
            });
            
            // Fetch organization settings
            await userStore.fetchOrganizationSettings();
          }
        }
      } catch (error) {
        console.error('Authentication error:', error);
      }
      
      // Listen for auth state changes with safeAuthStateChange to prevent deadlocks
      const authListener = safeAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // Get user profile with organization ID using safeSupabaseOperation
          try {
            const profileData = await safeSupabaseOperation(async () => {
              const { data } = await supabase
                .from('user_profiles')
                .select('org_id')
                .eq('user_id', session.user.id)
                .single();
              return data;
            });
              
            if (profileData?.org_id) {
              // Extract org_id as string explicitly
              const orgId: string = profileData.org_id as string;
              
              // Set user in store
              userStore.setUser({
                id: session.user.id,
                email: session.user.email || '',
                organization_id: orgId
              });
              
              // Fetch organization settings
              await userStore.fetchOrganizationSettings();
            }
          } catch (error) {
            console.error('Error fetching user profile:', error);
          }
        } else if (event === 'SIGNED_OUT') {
          userStore.setUser(null);
        }
      });
      
      // Setup page refresh handlers
      setupPageRefreshHandlers();
    });
    
    // Clean up handlers when component is unmounted
    watch(
      direction,
      (newDirection) => {
        document.body.setAttribute("dir", newDirection);
      },
      { immediate: true }
    );
    
    // When component is unmounted
    onMounted(() => {
      return () => {
        cleanupPageRefreshHandlers();
      };
    });

    return {
      // isLoading,
      shouldShowSidebar,
      shouldShowPaddingZero,
      shouldShowHeader,
      shouldShowDiv,
      shouldShowFooter,
      isNotFound,
      stateStoreInstance,
      userStore,
    };
  },
});
</script>
